import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { X, Zap, Check, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PremiumModal({ open, onClose, featureName = "this feature" }) {
  const { user, isPremium, getToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    if (!user) { navigate("/signup"); onClose(); return; }
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/payments/create-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const { checkout_url } = await res.json();
      window.location.href = checkout_url;
    } catch (e) {
      console.error("Checkout failed:", e);
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

        <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-[#18181B] border border-[#22c55e]/30 rounded-2xl p-8 max-w-md w-full shadow-2xl"
        >
          <button onClick={onClose} className="absolute top-4 right-4 text-[#52525B] hover:text-white">
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-[#22c55e]/10 border border-[#22c55e]/30 flex items-center justify-center">
              <Lock className="w-6 h-6 text-[#22c55e]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-black text-white">Upgrade to Premium</h2>
              <p className="text-sm text-[#A1A1AA]">Unlock {featureName}</p>
            </div>
          </div>

          {/* Price */}
          <div className="bg-[#22c55e]/5 border border-[#22c55e]/20 rounded-xl p-5 mb-6">
            <div className="flex items-baseline gap-1 mb-1">
              <span className="font-data text-4xl font-black text-[#22c55e]">$12.34</span>
              <span className="text-[#A1A1AA]">/ month</span>
            </div>
            <p className="text-xs text-[#52525B]">Cancel anytime · Billed monthly via Stripe</p>
          </div>

          {/* Features */}
          <div className="space-y-3 mb-6">
            {[
              { label: "β-Regression Forecast", desc: "Weather-driven stock price projection using regression coefficients" },
              { label: "Deep Pattern Matching", desc: "Historical analog search across 2+ years of data" },
              { label: "All 12 weather metrics", desc: "Including marine, UV, pressure and humidity layers" },
              { label: "365-day history scans", desc: "Analyse full-year correlations across all ASX sectors" },
              { label: "Saved searches & watchlist", desc: "Persist your cities, sectors and favourite stocks" },
            ].map(f => (
              <div key={f.label} className="flex items-start gap-3">
                <Check className="w-4 h-4 text-[#22c55e] shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-white">{f.label}</p>
                  <p className="text-xs text-[#52525B]">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <Button onClick={handleUpgrade} disabled={loading}
            className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-black font-bold h-12 text-base mb-3">
            {loading ? "Redirecting to payment..." : (
              <><Zap className="w-5 h-5 mr-2" />Upgrade to Premium — $12.34/mo</>
            )}
          </Button>

          {!user && (
            <Button onClick={() => { navigate("/login"); onClose(); }} variant="outline"
              className="w-full border-[#27272A] text-[#A1A1AA] hover:text-white h-10">
              Already have an account? Sign in
            </Button>
          )}

          <p className="text-center text-xs text-[#52525B] mt-4">
            🔒 Secure payment via Stripe · For informational use only · Not financial advice
          </p>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
